import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Box, Stack, Paper, Typography, alpha, TextField, InputAdornment, IconButton, Pagination } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Page from "../../../minimals/components/page";
import { useUserContext } from "../../contexts/UserContext";
import Iconify from "../../../minimals/components/iconify";
import { getLastBusinessDay, getTwoBusinessDaysAgo } from "./constants";
import { getInteractionKey } from "./utils/interactionKey";
import { useInteractionOrders } from "./hooks/useInteractionOrders";
import { useInteractionManager } from "./hooks/useInteractionManager";
import { useInteractionTypes } from "./hooks/useInteractionTypes";
import { useClientResponseInteractions } from "./hooks/useClientResponseInteractions";
import { useMissedInteractionOrders } from "./hooks/useMissedInteractionOrders";
import { DateFilter } from "./components/DateFilter";
import { StatusTabs } from "./components/StatusTabs";
import { InteractionTable } from "./components/InteractionTable";
import { ClientResponseTable } from "./components/ClientResponseTable";
import { SearchBar } from "./components/SearchBar";

const whitelistedUsers = [
  "agoldstein@madisoncres.com",
  "mgersten@madisoncres.com",
  "bfishman@madisoncres.com",
  "bsputz@madisoncres.com",
  "gmargareten@madisoncres.com",
  "mbecker@madisoncres.com",
  "ccelnik@madisoncres.com",
  "ymeister@madisontitle.com",
  "jshonek@madisontitle.com",
  "sdavidowitz@madisoncres.com",
  "swaldman@madisoncres.com",
  "fschwed@madisoncres.com",
  "bfreylicher@madisoncres.com",
  "mstern@madisontitle.com",
  "hwosner@madisontitle.com",
  "ffeigelstock@madisontitle.com",
  "jr@madisoncres.com"
];

const responseWhitelistedUsers = [
  "bsputz@madisoncres.com",
  "gmargareten@madisoncres.com",
  "fschwed@madisoncres.com",
  "jr@madisoncres.com",
  "sdavidowitz@madisoncres.com",
  "swaldman@madisoncres.com",
  "agoldstein@madisoncres.com",
  "mgersten@madisoncres.com"
];

export default function DailyOrdersClientInteractions() {
  const theme = useTheme();
  const { user, isUserLoading } = useUserContext();
  const isWhiteListedUser = useMemo(() => {
    return (user?.email && whitelistedUsers.includes(user?.email?.toLowerCase())) || false;
  }, [user]);
  const isResponseWhitelistedUser = useMemo(() => {
    return (user?.email && responseWhitelistedUsers.includes(user?.email?.toLowerCase())) || false;
  }, [user]);
  const [searchParams, setSearchParams] = useSearchParams();
  const orderNumbersParam = searchParams.get("orders");
  const linkedOrderNumbers = useMemo(
    () => orderNumbersParam?.split(",").map((number) => number.trim()).filter(Boolean) ?? [],
    [orderNumbersParam]
  );
  const pinnedOrderNumbers = useMemo(
    () => linkedOrderNumbers.length > 0 ? new Set(linkedOrderNumbers) : null,
    [linkedOrderNumbers]
  );

  const getInitialStatus = () => {
    const statusParam = searchParams.get("status");
    if (statusParam === "Received") return "Received";
    if (statusParam === "Response") return "Response";
    if (statusParam === "ClosedMissed") return "ClosedMissed";
    if (statusParam === "ReceivedMissed") return "ReceivedMissed";
    return "Closed";
  };

  const getInitialDate = () => {
    const initialStatus = getInitialStatus();
    return initialStatus === "Received" ? getTwoBusinessDaysAgo() : getLastBusinessDay();
  };

  const [statusFilter, setStatusFilter] = useState<string>(getInitialStatus());
  const [selectedDate, setSelectedDate] = useState<dayjs.Dayjs | null>(getInitialDate());

  const isMissedTab = statusFilter === "ClosedMissed" || statusFilter === "ReceivedMissed";
  const missedBaseStatus: "Closed" | "Received" = statusFilter === "ClosedMissed" ? "Closed" : "Received";

  const [searchQuery, setSearchQuery] = useState<string>("");
  const initialOrderNumber = searchParams.get("orderNumber")?.trim() || "";
  const [orderNumberSearch, setOrderNumberSearch] = useState<string>(initialOrderNumber);
  const [activeOrderNumberSearch, setActiveOrderNumberSearch] = useState<string | null>(
    initialOrderNumber || null
  );
  const [orderBy, setOrderBy] = useState<"orderDate" | "contactName" | null>(null);
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">("asc");

  const { interactionTypes, isLoading: isLoadingTypes } = useInteractionTypes();

  const {
    interactions: responseInteractions,
    isLoading: isLoadingResponse,
    error: responseError,
    fromDate,
    toDate,
    setFromDate,
    setToDate,
    updateResponse
  } = useClientResponseInteractions(isResponseWhitelistedUser);

  const {
    orders: fetchedOrders,
    isLoading,
    error,
    existingInteractions,
    existingSkippedInteractions,
    initialInteractions,
    taggedOrders,
    tagOrderContacts,
    untagContact,
    resolveContactTag,
    abort: abortOrders
  } = useInteractionOrders(
    isMissedTab ? null : activeOrderNumberSearch ? null : selectedDate,
    isMissedTab ? "Closed" : statusFilter,
    isMissedTab ? null : activeOrderNumberSearch,
    isMissedTab ? null : linkedOrderNumbers
  );

  const [orders, setOrders] = useState(fetchedOrders);

  const {
    interactions,
    setInteractions,
    handleInteractionTypeChange,
    handleInteractionNoteChange,
    handlePreInstructionsChange: originalHandlePreInstructionsChange,
    handleSkipWithReason,
    handleUndoInteraction,
    handleClosedConfirmationChange: originalHandleClosedConfirmationChange,
    setExistingInteractions,
    setExistingSkippedInteractions
  } = useInteractionManager({
    userId: user?.internalUser?.id,
    selectedDate,
    existingInteractions,
    existingSkippedInteractions,
    interactionCategory: isMissedTab ? missedBaseStatus : statusFilter
  });

  const {
    orders: missedFetchedOrders,
    isLoading: isMissedLoading,
    error: missedError,
    abort: abortMissedOrders
  } = useMissedInteractionOrders(missedBaseStatus, isMissedTab);

  const MISSED_PAGE_SIZE = 25;
  const [missedPage, setMissedPage] = useState(1);
  const [missedOrders, setMissedOrders] = useState(missedFetchedOrders);

  const {
    interactions: missedInteractions,
    handleInteractionTypeChange: handleMissedInteractionTypeChange,
    handleInteractionNoteChange: handleMissedInteractionNoteChange,
    handlePreInstructionsChange: originalHandleMissedPreInstructionsChange,
    handleSkipWithReason: handleMissedSkipWithReason,
    handleUndoInteraction: handleMissedUndoInteraction,
    handleClosedConfirmationChange: originalHandleMissedClosedConfirmationChange
  } = useInteractionManager({
    userId: user?.internalUser?.id,
    selectedDate: null,
    existingInteractions: {},
    existingSkippedInteractions: {},
    interactionCategory: missedBaseStatus
  });

  const missedPreInstructionsDebouncedRef = useRef<Record<string, NodeJS.Timeout>>({});

  const handleMissedPreInstructionsChange = useCallback(
    (orderNumber: string, instructions: string, contactId: number, settlementDate?: string | null) => {
      const key = `${orderNumber}-${contactId}`;
      if (missedPreInstructionsDebouncedRef.current[key]) {
        clearTimeout(missedPreInstructionsDebouncedRef.current[key]);
      }
      missedPreInstructionsDebouncedRef.current[key] = setTimeout(() => {
        setMissedOrders((prevOrders) =>
          prevOrders.map((order) =>
            order.orderNumber === orderNumber && order.phonebookContactId === contactId
              ? {
                  ...order,
                  preInteractionInstructions: instructions,
                  preInteractionInstructionsBy:
                    instructions && user?.internalUser
                      ? { firstName: user.internalUser.firstName, lastName: user.internalUser.lastName }
                      : instructions
                        ? order.preInteractionInstructionsBy
                        : null,
                  preInteractionInstructionsAt: instructions ? new Date().toISOString() : null
                }
              : order
          )
        );
        delete missedPreInstructionsDebouncedRef.current[key];
      }, 1000);
      originalHandleMissedPreInstructionsChange(orderNumber, instructions, contactId, settlementDate);
    },
    [user, originalHandleMissedPreInstructionsChange]
  );

  const handleMissedClosedConfirmationChange = useCallback(
    (orderNumber: string, isConfirmed: boolean) => {
      originalHandleMissedClosedConfirmationChange(orderNumber, isConfirmed);
      setMissedOrders((prev) =>
        prev.map((o) => (o.orderNumber === orderNumber ? { ...o, isClosedConfirmed: isConfirmed } : o))
      );
    },
    [originalHandleMissedClosedConfirmationChange]
  );

  useEffect(() => {
    setOrders(fetchedOrders);
  }, [fetchedOrders]);

  useEffect(() => {
    setMissedOrders(missedFetchedOrders);
    setMissedPage(1);
  }, [missedFetchedOrders]);

  const managerParam = searchParams.get("manager");
  const includeUnassignedParam = searchParams.get("includeUnassigned") === "true";
  const [managerClosers, setManagerClosers] = useState<Set<string> | null>(null);
  const [allManagedClosers, setAllManagedClosers] = useState<Set<string> | null>(null);
  const managerParamRef = useRef<string | null>(null);

  useEffect(() => {
    if (!managerParam || managerParam === managerParamRef.current) return;
    managerParamRef.current = managerParam;
    axios
      .get<string[]>(`/api/clientphonebook/internalUsers/getCloserNamesByManagerEmail?managerEmail=${encodeURIComponent(managerParam)}`)
      .then(({ data }) => setManagerClosers(new Set(data)));
  }, [managerParam]);

  useEffect(() => {
    if (!includeUnassignedParam) return;
    axios
      .get<string[]>("/api/clientphonebook/internalUsers/getAllManagedCloserNames")
      .then(({ data }) => setAllManagedClosers(new Set(data)));
  }, [includeUnassignedParam]);

  const filteredOrders = useMemo(() => {
    let filtered = orders;

    if (managerClosers) {
      filtered = filtered.filter((order) => {
        if (order.whoHasTheFile && managerClosers.has(order.whoHasTheFile)) return true;
        if (includeUnassignedParam && allManagedClosers) {
          return !order.whoHasTheFile || !allManagedClosers.has(order.whoHasTheFile);
        }
        return false;
      });
    }

    if (pinnedOrderNumbers) {
      filtered = filtered.filter((order) => order.orderNumber && pinnedOrderNumbers.has(order.orderNumber));
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = orders.filter((order) => {
        return (
          order.orderNumber?.toLowerCase().includes(query) ||
          order.contactName?.toLowerCase().includes(query) ||
          order.companyName?.toLowerCase().includes(query) ||
          order.preferredName?.toLowerCase().includes(query) ||
          order.allPropertiesInFile?.toLowerCase().includes(query) ||
          order.businessEmail?.toLowerCase().includes(query) ||
          order.customOrderStatus?.toLowerCase().includes(query)
        );
      });
    }

    if (orderBy) {
      filtered = [...filtered].sort((a, b) => {
        if (orderBy === "orderDate") {
          const aDate = statusFilter === "Closed" ? a.settlementDate : a.receivedDate;
          const bDate = statusFilter === "Closed" ? b.settlementDate : b.receivedDate;
          const parsedADate = aDate ? dayjs(aDate) : null;
          const parsedBDate = bDate ? dayjs(bDate) : null;
          const aTimestamp = parsedADate?.isValid() ? parsedADate.valueOf() : null;
          const bTimestamp = parsedBDate?.isValid() ? parsedBDate.valueOf() : null;

          if (aTimestamp === null && bTimestamp === null) return 0;
          if (aTimestamp === null) return 1;
          if (bTimestamp === null) return -1;

          const comparison = aTimestamp - bTimestamp;
          return orderDirection === "asc" ? comparison : -comparison;
        }

        const aValue = a.contactName || "";
        const bValue = b.contactName || "";
        const comparison = aValue.localeCompare(bValue);
        return orderDirection === "asc" ? comparison : -comparison;
      });
    }

    return filtered;
  }, [orders, searchQuery, orderBy, orderDirection, statusFilter, pinnedOrderNumbers, managerClosers, includeUnassignedParam, allManagedClosers]);

  const filteredTaggedOrders = useMemo(() => {
    let filtered = taggedOrders;

    if (managerClosers) {
      filtered = filtered.filter((order) => {
        if (order.whoHasTheFile && managerClosers.has(order.whoHasTheFile)) return true;
        if (includeUnassignedParam && allManagedClosers) {
          return !order.whoHasTheFile || !allManagedClosers.has(order.whoHasTheFile);
        }
        return false;
      });
    }

    if (pinnedOrderNumbers) {
      filtered = filtered.filter((order) => order.orderNumber && pinnedOrderNumbers.has(order.orderNumber));
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((order) => {
        return (
          order.orderNumber?.toLowerCase().includes(query) ||
          order.contactName?.toLowerCase().includes(query) ||
          order.companyName?.toLowerCase().includes(query) ||
          order.preferredName?.toLowerCase().includes(query) ||
          order.allPropertiesInFile?.toLowerCase().includes(query) ||
          order.businessEmail?.toLowerCase().includes(query) ||
          order.customOrderStatus?.toLowerCase().includes(query)
        );
      });
    }

    return [...filtered].sort((a, b) => {
      if (orderBy === "contactName") {
        const comparison = (a.contactName || "").localeCompare(b.contactName || "");
        return orderDirection === "asc" ? comparison : -comparison;
      }

      const parsedADate = a.settlementDate ? dayjs(a.settlementDate) : null;
      const parsedBDate = b.settlementDate ? dayjs(b.settlementDate) : null;
      const aTimestamp = parsedADate?.isValid() ? parsedADate.valueOf() : null;
      const bTimestamp = parsedBDate?.isValid() ? parsedBDate.valueOf() : null;

      if (aTimestamp === null && bTimestamp === null) return 0;
      if (aTimestamp === null) return 1;
      if (bTimestamp === null) return -1;

      const comparison = aTimestamp - bTimestamp;
      const direction = orderBy === "orderDate" ? orderDirection : "asc";
      return direction === "asc" ? comparison : -comparison;
    });
  }, [
    taggedOrders,
    searchQuery,
    pinnedOrderNumbers,
    managerClosers,
    includeUnassignedParam,
    allManagedClosers,
    orderBy,
    orderDirection
  ]);

  const missedFilteredOrders = useMemo(() => {
    let filtered = missedOrders;

    if (managerClosers) {
      filtered = filtered.filter((order) => {
        if (order.whoHasTheFile && managerClosers.has(order.whoHasTheFile)) return true;
        if (includeUnassignedParam && allManagedClosers) {
          return !order.whoHasTheFile || !allManagedClosers.has(order.whoHasTheFile);
        }
        return false;
      });
    }

    if (pinnedOrderNumbers) {
      filtered = filtered.filter(
        (order) => order.orderNumber && pinnedOrderNumbers.has(order.orderNumber)
      );
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((order) => {
        return (
          order.orderNumber?.toLowerCase().includes(query) ||
          order.contactName?.toLowerCase().includes(query) ||
          order.companyName?.toLowerCase().includes(query) ||
          order.preferredName?.toLowerCase().includes(query) ||
          order.allPropertiesInFile?.toLowerCase().includes(query) ||
          order.businessEmail?.toLowerCase().includes(query) ||
          order.customOrderStatus?.toLowerCase().includes(query)
        );
      });
    }

    if (activeOrderNumberSearch) {
      filtered = filtered.filter((order) =>
        order.orderNumber?.toLowerCase().includes(activeOrderNumberSearch.toLowerCase())
      );
    }

    if (orderBy) {
      filtered = [...filtered].sort((a, b) => {
        if (orderBy === "orderDate") {
          const aDate = missedBaseStatus === "Closed" ? a.settlementDate : a.receivedDate;
          const bDate = missedBaseStatus === "Closed" ? b.settlementDate : b.receivedDate;
          const parsedADate = aDate ? dayjs(aDate) : null;
          const parsedBDate = bDate ? dayjs(bDate) : null;
          const aTimestamp = parsedADate?.isValid() ? parsedADate.valueOf() : null;
          const bTimestamp = parsedBDate?.isValid() ? parsedBDate.valueOf() : null;

          if (aTimestamp === null && bTimestamp === null) return 0;
          if (aTimestamp === null) return 1;
          if (bTimestamp === null) return -1;

          const comparison = aTimestamp - bTimestamp;
          return orderDirection === "asc" ? comparison : -comparison;
        }

        const aValue = a.contactName || "";
        const bValue = b.contactName || "";
        const comparison = aValue.localeCompare(bValue);

        return orderDirection === "asc" ? comparison : -comparison;
      });
    }

    return filtered;
  }, [
    missedOrders,
    searchQuery,
    activeOrderNumberSearch,
    pinnedOrderNumbers,
    managerClosers,
    includeUnassignedParam,
    allManagedClosers,
    missedBaseStatus,
    orderBy,
    orderDirection
  ]);

  const missedPageCount = Math.ceil(missedFilteredOrders.length / MISSED_PAGE_SIZE);

  useEffect(() => {
    setMissedPage(1);
  }, [
    searchQuery,
    activeOrderNumberSearch,
    pinnedOrderNumbers,
    managerClosers,
    includeUnassignedParam,
    allManagedClosers,
    missedBaseStatus,
    orderBy,
    orderDirection
  ]);

  useEffect(() => {
    setMissedPage((currentPage) => Math.min(currentPage, Math.max(1, missedPageCount)));
  }, [missedPageCount]);

  const missedPagedOrders = useMemo(
    () => missedFilteredOrders.slice((missedPage - 1) * MISSED_PAGE_SIZE, missedPage * MISSED_PAGE_SIZE),
    [missedFilteredOrders, missedPage]
  );

  useEffect(() => {
    setInteractions(initialInteractions);
  }, [initialInteractions, setInteractions]);

  const preInstructionsDebouncedRef = useRef<Record<string, NodeJS.Timeout>>({});

  const handlePreInstructionsChange = useCallback(
    (orderNumber: string, instructions: string, contactId: number, settlementDate?: string | null) => {
      const key = `${orderNumber}-${contactId}`;
      if (preInstructionsDebouncedRef.current[key]) {
        clearTimeout(preInstructionsDebouncedRef.current[key]);
      }
      preInstructionsDebouncedRef.current[key] = setTimeout(() => {
        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order.orderNumber === orderNumber && order.phonebookContactId === contactId
              ? {
                  ...order,
                  preInteractionInstructions: instructions,
                  preInteractionInstructionsBy:
                    instructions && user?.internalUser
                      ? { firstName: user.internalUser.firstName, lastName: user.internalUser.lastName }
                      : instructions
                        ? order.preInteractionInstructionsBy
                        : null,
                  preInteractionInstructionsAt: instructions ? new Date().toISOString() : null
                }
              : order
          )
        );
        delete preInstructionsDebouncedRef.current[key];
      }, 1000);
      originalHandlePreInstructionsChange(orderNumber, instructions, contactId, settlementDate);
    },
    [user, originalHandlePreInstructionsChange]
  );

  const handleClosedConfirmationChange = useCallback((orderNumber: string, isConfirmed: boolean) => {
    originalHandleClosedConfirmationChange(orderNumber, isConfirmed);
    setOrders((prev) =>
      prev.map((o) =>
        o.orderNumber === orderNumber ? { ...o, isClosedConfirmed: isConfirmed } : o
      )
    );
  }, [originalHandleClosedConfirmationChange]);

  const handleInteractionTypeChangeWithTagResolve = useCallback(
    (
      orderNumber: string,
      type: string,
      contactId: number,
      preInstructions?: string,
      settlementDate?: string | null
    ) => {
      handleInteractionTypeChange(orderNumber, type, contactId, preInstructions, settlementDate);
      if (statusFilter === "Closed" && type) {
        void resolveContactTag(orderNumber, contactId).catch(() => {});
      }
    },
    [handleInteractionTypeChange, resolveContactTag, statusFilter]
  );

  const handleTagOrderContacts = useCallback(
    (orderNumber: string) => {
      const contactIds = [...orders, ...taggedOrders]
        .filter((order) => {
          if (order.orderNumber !== orderNumber || !order.phonebookContactId) return false;

          const key = getInteractionKey(order.orderNumber, order.phonebookContactId);
          const interaction = interactions[key];
          const hasCompletedInteraction =
            Boolean(existingInteractions[key]) ||
            Boolean(existingSkippedInteractions[key]) ||
            Boolean(interaction?.typeId) ||
            Boolean(interaction?.skipped);

          return !hasCompletedInteraction;
        })
        .map((order) => order.phonebookContactId!) as number[];

      void tagOrderContacts(orderNumber, contactIds).catch(() => {});
    },
    [orders, taggedOrders, interactions, existingInteractions, existingSkippedInteractions, tagOrderContacts]
  );

  const handleUntagContact = useCallback(
    (orderNumber: string, contactId: number) => {
      void untagContact(orderNumber, contactId).catch(() => {});
    },
    [untagContact]
  );

  const isContactTaggedForFollowUp = useCallback(
    (orderNumber: string, contactId: number) => {
      return [...orders, ...taggedOrders].some(
        (order) =>
          order.orderNumber === orderNumber &&
          order.phonebookContactId === contactId &&
          order.isTaggedForFollowUp
      );
    },
    [orders, taggedOrders]
  );

  const handleSkipWithReasonWrapper = useCallback(
    (orderNumber: string, reason: string, contactId: number, settlementDate?: string | null) => {
      handleSkipWithReason(orderNumber, reason, contactId, settlementDate);
      if (statusFilter === "Closed" && isContactTaggedForFollowUp(orderNumber, contactId)) {
        void resolveContactTag(orderNumber, contactId).catch(() => {});
      }

      if (reason.toLowerCase() === "multisite") {
        const baseNumber = orderNumber.substring(0, 6);
        orders.forEach((order) => {
          if (!order.orderNumber || !order.phonebookContactId) return;
          if (order.orderNumber === orderNumber && order.phonebookContactId === contactId) return;
          if (order.phonebookContactId !== contactId) return;
          if (order.orderNumber.substring(0, 6) !== baseNumber) return;
          const key = getInteractionKey(order.orderNumber, order.phonebookContactId);
          if (existingInteractions[key] || existingSkippedInteractions[key] || interactions[key]) return;
          handleSkipWithReason(order.orderNumber, reason, order.phonebookContactId, order.settlementDate);
          if (statusFilter === "Closed" && order.isTaggedForFollowUp) {
            void resolveContactTag(order.orderNumber, order.phonebookContactId).catch(() => {});
          }
        });
      }
    },
    [
      handleSkipWithReason,
      orders,
      existingInteractions,
      existingSkippedInteractions,
      interactions,
      isContactTaggedForFollowUp,
      resolveContactTag,
      statusFilter
    ]
  );

  const handleMissedSkipWithReasonWrapper = useCallback(
    (orderNumber: string, reason: string, contactId: number, settlementDate?: string | null) => {
      handleMissedSkipWithReason(orderNumber, reason, contactId, settlementDate);

      if (reason.toLowerCase() === "multisite") {
        const baseNumber = orderNumber.substring(0, 6);
        missedOrders.forEach((order) => {
          if (!order.orderNumber || !order.phonebookContactId) return;
          if (order.orderNumber === orderNumber && order.phonebookContactId === contactId) return;
          if (order.phonebookContactId !== contactId) return;
          if (order.orderNumber.substring(0, 6) !== baseNumber) return;
          const key = getInteractionKey(order.orderNumber, order.phonebookContactId);
          if (missedInteractions[key]) return;
          handleMissedSkipWithReason(order.orderNumber, reason, order.phonebookContactId, order.settlementDate);
        });
      }
    },
    [handleMissedSkipWithReason, missedOrders, missedInteractions]
  );

  useEffect(() => {
    setExistingInteractions(existingInteractions);
  }, [existingInteractions, setExistingInteractions]);

  useEffect(() => {
    setExistingSkippedInteractions(existingSkippedInteractions);
  }, [existingSkippedInteractions, setExistingSkippedInteractions]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set("status", statusFilter);
    if (activeOrderNumberSearch) {
      params.set("orderNumber", activeOrderNumberSearch);
    } else {
      params.delete("orderNumber");
    }
    setSearchParams(params, { replace: true });
  }, [statusFilter, activeOrderNumberSearch, setSearchParams]);

  const handleStatusFilterChange = (event: React.SyntheticEvent, newValue: string) => {
    abortOrders();
    abortMissedOrders();
    setStatusFilter(newValue);
    if (newValue !== "Response" && newValue !== "ClosedMissed" && newValue !== "ReceivedMissed") {
      setSelectedDate(newValue === "Received" ? getTwoBusinessDaysAgo() : getLastBusinessDay());
    }
  };

  const handleDateChange = (newValue: dayjs.Dayjs | null) => {
    setSelectedDate(newValue);
    setOrderNumberSearch("");
    setActiveOrderNumberSearch(null);
  };

  const handleOrderNumberSearchSubmit = () => {
    const orderNumber = orderNumberSearch.trim();
    if (orderNumber.length === 0) {
      handleClearOrderNumberSearch();
      return;
    }
    setActiveOrderNumberSearch(orderNumber);
  };

  const handleClearOrderNumberSearch = () => {
    setOrderNumberSearch("");
    setActiveOrderNumberSearch(null);
  };

  const handleSort = (column: "orderDate" | "contactName") => {
    if (orderBy === column) {
      setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(column);
      setOrderDirection("asc");
    }
  };

  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case "Closed":
        return theme.palette.success.main;
      case "Received":
        return theme.palette.info.main;
      default:
        return theme.palette.grey[500];
    }
  }, [theme]);

  if (!isUserLoading && !isWhiteListedUser) {
    return (
      <Page title="Client Interactions">
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.background.default, 0.5),
            maxWidth: 600,
            mx: "auto",
            mt: 8
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.error.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <Iconify icon="eva:lock-fill" width={40} sx={{ color: theme.palette.error.main }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Access Denied
              </Typography>
              <Typography variant="body1" color="text.secondary">
                You do not have permission to access Client Interactions.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Page>
    );
  }

  return (
    <Page title="Client Interactions">
      <Box sx={{ p: { xs: 2, sm: 2, md: 0 }, maxWidth: 1800, mx: "auto" }}>
        <Stack spacing={2.5}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "column", md: "row" },
              justifyContent: { md: "space-between" },
              alignItems: { md: "flex-end" },
              gap: 2
            }}
          >
            <Box>
              <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5, letterSpacing: "-0.02em" }}>
                Client Interactions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400 }}>
                Track and manage client deal interactions
              </Typography>
            </Box>

            {statusFilter === "Response" ? (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Stack
                  direction={{ xs: "column", sm: "column", md: "row" }}
                  spacing={2}
                  sx={{
                    alignItems: { xs: "stretch", sm: "stretch", md: "center" },
                    width: { xs: "100%", sm: "100%", md: "auto" }
                  }}
                >
                  <Box sx={{ width: { xs: "100%", sm: "100%", md: 180 } }}>
                    <DatePicker
                      label="From"
                      value={fromDate}
                      onChange={(val) => val && setFromDate(val)}
                      maxDate={toDate}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          sx: { "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } }
                        }
                      }}
                    />
                  </Box>
                  <Box sx={{ width: { xs: "100%", sm: "100%", md: 180 } }}>
                    <DatePicker
                      label="To"
                      value={toDate}
                      onChange={(val) => val && setToDate(val)}
                      minDate={fromDate}
                      maxDate={dayjs()}
                      slotProps={{
                        textField: {
                          size: "small",
                          fullWidth: true,
                          sx: { "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } }
                        }
                      }}
                    />
                  </Box>
                </Stack>
              </LocalizationProvider>
            ) : (
              <Stack
                direction={{ xs: "column", sm: "column", md: "row" }}
                spacing={2}
                sx={{
                  alignItems: { xs: "stretch", sm: "stretch", md: "center" },
                  width: { xs: "100%", sm: "100%", md: "auto" }
                }}
              >
                <Box sx={{ width: { xs: "100%", sm: "100%", md: 300 } }}>
                  <TextField
                    label="Order #"
                    size="small"
                    fullWidth
                    value={orderNumberSearch}
                    onChange={(e) => {
                      const newValue = e.target.value;
                      setOrderNumberSearch(newValue);
                      if (newValue === "" && activeOrderNumberSearch) {
                        setActiveOrderNumberSearch(null);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleOrderNumberSearchSubmit();
                      }
                    }}
                    placeholder="e.g. 202294"
                    InputProps={{
                      endAdornment: activeOrderNumberSearch ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={handleClearOrderNumberSearch} edge="end">
                            <Iconify icon="eva:close-fill" width={18} />
                          </IconButton>
                        </InputAdornment>
                      ) : orderNumberSearch ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={handleOrderNumberSearchSubmit} edge="end">
                            <Iconify icon="eva:search-fill" width={18} />
                          </IconButton>
                        </InputAdornment>
                      ) : null
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "background.paper"
                      }
                    }}
                  />
                </Box>
                {!isMissedTab && (
                  <Box sx={{ width: { xs: "100%", sm: "100%", md: 180 } }}>
                    <DateFilter
                      selectedDate={selectedDate}
                      onChange={handleDateChange}
                      disabled={!!activeOrderNumberSearch}
                    />
                  </Box>
                )}
              </Stack>
            )}
          </Box>

          <Paper
            elevation={0}
            sx={{
              overflow: "hidden",
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: "background.paper"
            }}
          >
            <StatusTabs
              statusFilter={statusFilter}
              onChange={handleStatusFilterChange}
              showResponseTab={isResponseWhitelistedUser}
            />
            {statusFilter === "Response" ? (
              <ClientResponseTable
                interactions={responseInteractions}
                isLoading={isLoadingResponse}
                error={responseError}
                onUpdateResponse={updateResponse}
              />
            ) : isMissedTab ? (
              <>
                <InteractionTable
                  orders={missedPagedOrders}
                  interactions={missedInteractions}
                  isLoading={isMissedLoading || isLoadingTypes}
                error={missedError}
                statusFilter={missedBaseStatus}
                useClosedTabOrderLayout={false}
                interactionTypes={interactionTypes}
                  onInteractionTypeChange={handleMissedInteractionTypeChange}
                  onInteractionNoteChange={handleMissedInteractionNoteChange}
                  onPreInstructionsChange={handleMissedPreInstructionsChange}
                  onSkipWithReason={handleMissedSkipWithReasonWrapper}
                  onUndoInteraction={handleMissedUndoInteraction}
                  onClosedConfirmationChange={handleMissedClosedConfirmationChange}
                  getStatusColor={getStatusColor}
                  orderBy={orderBy}
                  orderDirection={orderDirection}
                  onSort={handleSort}
                />
                {missedPageCount > 1 && (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        {missedFilteredOrders.length} contacts · page {missedPage} of {missedPageCount}
                      </Typography>
                      <Pagination
                        count={missedPageCount}
                        page={missedPage}
                        onChange={(_, page) => setMissedPage(page)}
                        size="small"
                        color="primary"
                      />
                    </Stack>
                  </Box>
                )}
              </>
            ) : (
              <InteractionTable
                orders={filteredOrders}
                taggedOrders={filteredTaggedOrders}
                interactions={interactions}
                isLoading={isLoading || isLoadingTypes || (fetchedOrders.length > 0 && orders.length === 0)}
                error={error}
                statusFilter={statusFilter}
                useClosedTabOrderLayout={statusFilter === "Closed"}
                interactionTypes={interactionTypes}
                onInteractionTypeChange={handleInteractionTypeChangeWithTagResolve}
                onInteractionNoteChange={handleInteractionNoteChange}
                onPreInstructionsChange={handlePreInstructionsChange}
                onSkipWithReason={handleSkipWithReasonWrapper}
                onUndoInteraction={handleUndoInteraction}
                onClosedConfirmationChange={handleClosedConfirmationChange}
                getStatusColor={getStatusColor}
                orderBy={orderBy}
                orderDirection={orderDirection}
                onSort={handleSort}
                onTagOrderContacts={handleTagOrderContacts}
                onUntagContact={handleUntagContact}
              />
            )}
          </Paper>
        </Stack>
      </Box>
    </Page>
  );
}
